import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { useFileUpload } from "@/lib/uploadProgress";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Download, Mail, Plus, Trash2 } from "lucide-react";

const EMPTY_ITEM = { description: "", hsn: "", gross_wt: "", net_wt: "", pieces: "1", qty: 1, rate: 0, making: 0, discount: 0, gst_percent: 0 };
const EMPTY_SIMPLE_ITEM = { name: "", description: "", amount: "" };
const EMPTY_OLD_GOLD = { description: "", gross_wt: "", purity: "", net_wt: "", rate: "", value: "" };
const EMPTY_CUSTOMER = { customer_id: "", name: "", email: "", mobile: "", address: "", state: "", state_code: "", pan: "" };
const EMPTY_META = { date: "", due_date: "", place_of_supply: "", supply_state_code: "", transaction_type: "sale", payment_mode: "", ref_no: "" };
const PAY_MODES = ["Cash", "UPI", "Bank Transfer", "Card", "Other"];

function today() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export default function AdminInvoiceMaker() {
  const upload = useFileUpload();
  const [active, setActive] = useState(false);
  const [tab, setTab] = useState("list");
  const [settings, setSettings] = useState(null);
  const [used, setUsed] = useState(0);
  const [limit, setLimit] = useState(100);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState({});
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);

  const load = async () => {
    const { data } = await api.get("/merchant/invoice-maker/settings");
    setActive(true);
    setSettings(data.settings);
    setUsed(data.used);
    setLimit(data.limit);
    const list = await api.get("/merchant/invoice-maker");
    setItems(list.data.items || []);
    setUsed(list.data.used);
  };

  useEffect(() => {
    api.get("/merchant/customers").then(({ data }) => setCustomers(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    load()
      .catch((e) => {
        const msg = extractErr(e);
        if (String(msg).toLowerCase().includes("not active") || e?.response?.status === 402) setActive(false);
        else toast.error(msg);
      })
      .finally(() => setReady(true));
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/merchant/invoice-maker/settings", settings);
      setSettings(data.settings);
      toast.success("Invoice design saved");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };

  const onImage = async (design, field, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "invoice");
    try {
      const data = await upload("/merchant/media/upload", fd);
      setSettings((s) => ({ ...s, [design]: { ...s[design], [field]: data.url } }));
    } catch (e) { toast.error(extractErr(e)); }
  };

  const startNew = () => {
    if (used >= limit) {
      toast.error(`Limit of ${limit} invoices reached. Delete an old invoice first.`);
      return;
    }
    const design = settings?.active_design === "universal" ? "universal" : "jewellery";
    setEditing({
      id: null,
      design,
      customer: { ...EMPTY_CUSTOMER },
      meta: { ...EMPTY_META, date: today() },
      items: [design === "universal" ? { ...EMPTY_SIMPLE_ITEM } : { ...EMPTY_ITEM }],
      old_gold: design === "jewellery" ? [{ ...EMPTY_OLD_GOLD }] : [],
      round_off: 0,
      received: 0,
      upi_qr_url: "",
      gst_enabled: false,
      gst_percent: "",
    });
    setTab("edit");
  };

  const openExisting = async (id) => {
    try {
      const { data } = await api.get(`/merchant/invoice-maker/${id}`);
      setEditing({
        id: data.id,
        design: data.design,
        customer: data.customer || { ...EMPTY_CUSTOMER },
        meta: data.meta || { ...EMPTY_META },
        items: data.items?.length ? data.items : [{ ...EMPTY_ITEM }],
        old_gold: data.old_gold?.length ? data.old_gold : [{ description: "", gross_wt: "", purity: "", net_wt: "", rate: "", value: "" }],
        round_off: data.round_off || 0,
        received: data.received || 0,
        gst_enabled: Boolean(data.gst_enabled),
        gst_percent: data.gst_percent ?? "",
        upi_qr_url: data.upi_qr_url || "",
        link: data.link,
        number: data.number,
      });
      setTab("edit");
    } catch (e) { toast.error(extractErr(e)); }
  };

  const saveInvoice = async () => {
    if (!editing.customer.name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        design: editing.design,
        customer: editing.customer,
        meta: editing.meta,
        items: editing.items,
        old_gold: editing.design === "jewellery" ? (editing.old_gold || []) : [],
        gst_enabled: editing.design === "universal" ? Boolean(editing.gst_enabled) : false,
        gst_percent: editing.design === "universal" ? Number(editing.gst_percent) || 0 : 0,
        round_off: Number(editing.round_off) || 0,
        received: Number(editing.received) || 0,
        upi_qr_url: editing.upi_qr_url || "",
      };
      const { data } = editing.id
        ? await api.put(`/merchant/invoice-maker/${editing.id}`, payload)
        : await api.post("/merchant/invoice-maker", payload);
      toast.success(editing.id ? "Invoice updated" : "Invoice created");
      setEditing((e) => ({ ...e, id: data.id, number: data.number, link: data.link }));
      await load();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };

  const emailInvoice = async () => {
    if (!editing?.id) {
      toast.error("Save the invoice before emailing it");
      return;
    }
    try {
      await api.post(`/merchant/invoice-maker/${editing.id}/email`, { to: editing.customer.email });
      toast.success("Invoice email sent");
    } catch (e) { toast.error(extractErr(e)); }
  };

  const ids = Object.keys(selected).filter((id) => selected[id]);

  const bulkDelete = async () => {
    if (!ids.length) return;
    try {
      await api.post("/merchant/invoice-maker/bulk-delete", { ids });
      toast.success("Invoices deleted");
      setSelected({});
      await load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const bulkZip = async () => {
    if (!ids.length) return;
    try {
      const res = await api.post("/merchant/invoice-maker/bulk-zip", { ids }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "invoices.zip";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(extractErr(e)); }
  };

  if (!ready) return <div className="text-slate-500">Loading…</div>;
  if (!active || !settings) {
    return (
      <div className="max-w-2xl space-y-4">
        <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> App Store</Link>
        <Card className="p-8 text-center text-slate-600">Unlock <strong>Invoice Maker</strong> from the App Store to create invoices.</Card>
      </div>
    );
  }

  const setBrand = (design, field, value) => setSettings((s) => ({ ...s, [design]: { ...s[design], [field]: value } }));
  const activeDesign = settings.active_design === "universal" ? "universal" : "jewellery";
  const chooseDesign = async (design) => {
    const next = { ...settings, active_design: design };
    setSettings(next);
    setSaving(true);
    try {
      const { data } = await api.put("/merchant/invoice-maker/settings", next);
      setSettings(data.settings);
      toast.success(design === "jewellery" ? "Jewellery design is now used for new bills" : "Universal design is now used for new bills");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };
  const modeOn = (mode, label) => String(mode || "").toLowerCase().split(",").some((part) => part.trim() === label.toLowerCase() || (label === "Bank Transfer" && part.includes("bank")));
  const toggleMode = (label) => {
    const parts = String(editing.meta.payment_mode || "").split(",").map((s) => s.trim()).filter(Boolean);
    const on = modeOn(editing.meta.payment_mode, label);
    const next = on ? parts.filter((p) => p.toLowerCase() !== label.toLowerCase() && !(label === "Bank Transfer" && p.toLowerCase().includes("bank"))) : [...parts, label];
    setEditing({ ...editing, meta: { ...editing.meta, payment_mode: next.join(", ") } });
  };

  return (
    <div className="max-w-5xl space-y-5" data-testid="invoice-maker">
      <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> App Store</Link>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Invoice Maker</h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="invoice-maker-quota">{used} of {limit} invoices used. Delete an old invoice to free a slot.</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 border-b border-slate-200">
        {[
          ["list", "Invoices", "im-tab-list"],
          ["universal", "Universal design", "im-tab-universal"],
          ["jewellery", "Jewellery design", "im-tab-jewellery"],
        ].map(([id, label, testId]) => (
          <button key={id} type="button" data-testid={testId} onClick={() => setTab(id)} className={`px-4 py-2.5 text-sm font-semibold -mb-px border-b-2 ${tab === id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500"}`}>{label}</button>
        ))}
      </div>

      {tab === "list" && (
        <Card className="p-0 overflow-hidden border-slate-200/70">
          <div className="flex flex-wrap items-center gap-2 p-4 border-b border-slate-100">
            <Button onClick={startNew} data-testid="im-new" className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1" /> New invoice</Button>
            <Button variant="outline" disabled={!ids.length} onClick={bulkZip} data-testid="im-bulk-zip"><Download className="w-4 h-4 mr-1" /> Download ZIP</Button>
            <Button variant="outline" disabled={!ids.length} onClick={bulkDelete} data-testid="im-bulk-delete"><Trash2 className="w-4 h-4 mr-1" /> Delete selected</Button>
          </div>
          {items.length === 0 ? <div className="p-8 text-sm text-slate-500 text-center">No invoices yet.</div> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3" />
                  <th className="px-4 py-3">Invoice</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Design</th>
                  <th className="px-4 py-3">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((row) => (
                  <tr key={row.id} data-testid={`im-row-${row.id}`}>
                    <td className="px-4 py-3"><input type="checkbox" checked={!!selected[row.id]} onChange={(e) => setSelected((s) => ({ ...s, [row.id]: e.target.checked }))} data-testid={`im-select-${row.id}`} /></td>
                    <td className="px-4 py-3"><button type="button" className="font-semibold text-blue-700" onClick={() => openExisting(row.id)}>{row.number}</button></td>
                    <td className="px-4 py-3">{row.customer?.name}</td>
                    <td className="px-4 py-3 capitalize">{row.design}</td>
                    <td className="px-4 py-3 font-mono-nums">₹{Number(row.totals?.grand_total || 0).toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {tab === "edit" && editing && (
        <Card className="p-6 space-y-4 border-slate-200/70" data-testid="im-editor">
          <p className="text-sm text-slate-500" data-testid="im-active-design">This bill uses the <span className="font-semibold text-slate-800 capitalize">{editing.design}</span> design. Pick the design once under Universal design or Jewellery design — it is used for every new bill.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Existing customer</Label>
              <select className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="im-customer-pick" value={editing.customer.customer_id || ""} onChange={(e) => {
                const c = customers.find((x) => x.id === e.target.value);
                if (!c) return setEditing({ ...editing, customer: { ...editing.customer, customer_id: "" } });
                setEditing({ ...editing, customer: { ...editing.customer, customer_id: c.id, name: c.name || "", email: c.email || "", mobile: c.phone || "", address: c.address || "" } });
              }}>
                <option value="">New customer</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              ["name", "Customer name"], ["email", "Email"], ["mobile", "Mobile"], ["address", "Address"],
              ["state", "State"], ["state_code", "State code"], ["pan", "PAN"],
            ].map(([key, label]) => (
              <div key={key}>
                <Label>{label}</Label>
                <Input className="mt-1" data-testid={`im-cust-${key}`} value={editing.customer[key] || ""} onChange={(e) => setEditing({ ...editing, customer: { ...editing.customer, [key]: e.target.value } })} />
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label>Date</Label><Input type="date" className="mt-1" data-testid="im-date" value={editing.meta.date || ""} onChange={(e) => setEditing({ ...editing, meta: { ...editing.meta, date: e.target.value } })} /></div>
            <div><Label>Due date</Label><Input type="date" className="mt-1" value={editing.meta.due_date || ""} onChange={(e) => setEditing({ ...editing, meta: { ...editing.meta, due_date: e.target.value } })} /></div>
            {editing.design === "jewellery" && (
              <div>
                <Label>Transaction type</Label>
                <select className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="im-tx" value={editing.meta.transaction_type} onChange={(e) => setEditing({ ...editing, meta: { ...editing.meta, transaction_type: e.target.value } })}>
                  <option value="sale">Sale</option>
                  <option value="old_gold_exchange">Old gold exchange</option>
                  <option value="repair">Repair</option>
                </select>
              </div>
            )}
            <div><Label>Place of supply</Label><Input className="mt-1" value={editing.meta.place_of_supply || ""} onChange={(e) => setEditing({ ...editing, meta: { ...editing.meta, place_of_supply: e.target.value } })} /></div>
            {editing.design === "jewellery" ? (
              <div className="sm:col-span-2">
                <Label>Payment mode</Label>
                <div className="mt-2 flex flex-wrap gap-3" data-testid="im-pay-modes">
                  {PAY_MODES.map((label) => (
                    <label key={label} className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input type="checkbox" checked={modeOn(editing.meta.payment_mode, label)} onChange={() => toggleMode(label)} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <div><Label>Payment mode</Label><Input className="mt-1" value={editing.meta.payment_mode || ""} onChange={(e) => setEditing({ ...editing, meta: { ...editing.meta, payment_mode: e.target.value } })} /></div>
            )}
            <div><Label>Ref no.</Label><Input className="mt-1" value={editing.meta.ref_no || ""} onChange={(e) => setEditing({ ...editing, meta: { ...editing.meta, ref_no: e.target.value } })} /></div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Products</Label>
              <Button type="button" size="sm" variant="outline" data-testid="im-add-line" onClick={() => setEditing({ ...editing, items: [...editing.items, editing.design === "universal" ? { ...EMPTY_SIMPLE_ITEM } : { ...EMPTY_ITEM }] })}><Plus className="w-4 h-4 mr-1" /> Add line</Button>
            </div>
            {editing.design === "universal" ? editing.items.map((line, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-6 gap-2 p-3 rounded-xl border border-slate-200" data-testid={`im-line-${i}`}>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Product name</Label>
                  <Input data-testid={`im-line-name-${i}`} value={line.name || ""} onChange={(e) => { const items = [...editing.items]; items[i] = { ...line, name: e.target.value }; setEditing({ ...editing, items }); }} />
                </div>
                <div className="sm:col-span-3">
                  <Label className="text-xs">Description</Label>
                  <Input value={line.description || ""} onChange={(e) => { const items = [...editing.items]; items[i] = { ...line, description: e.target.value }; setEditing({ ...editing, items }); }} />
                </div>
                <div>
                  <Label className="text-xs">Amount</Label>
                  <Input type="number" data-testid={`im-line-amount-${i}`} value={line.amount ?? ""} onChange={(e) => { const items = [...editing.items]; items[i] = { ...line, amount: e.target.value }; setEditing({ ...editing, items }); }} />
                </div>
              </div>
            )) : editing.items.map((line, i) => (
              <div key={i} className="grid grid-cols-2 sm:grid-cols-6 gap-2 p-3 rounded-xl border border-slate-200" data-testid={`im-line-${i}`}>
                <div className="sm:col-span-2"><Label className="text-xs">Description</Label><Input value={line.description} onChange={(e) => { const items = [...editing.items]; items[i] = { ...line, description: e.target.value }; setEditing({ ...editing, items }); }} /></div>
                <div><Label className="text-xs">HSN</Label><Input value={line.hsn || ""} onChange={(e) => { const items = [...editing.items]; items[i] = { ...line, hsn: e.target.value }; setEditing({ ...editing, items }); }} /></div>
                <div><Label className="text-xs">Qty</Label><Input type="number" value={line.qty} onChange={(e) => { const items = [...editing.items]; items[i] = { ...line, qty: e.target.value, pieces: e.target.value }; setEditing({ ...editing, items }); }} /></div>
                <div><Label className="text-xs">Rate</Label><Input type="number" value={line.rate} onChange={(e) => { const items = [...editing.items]; items[i] = { ...line, rate: e.target.value }; setEditing({ ...editing, items }); }} /></div>
                <div><Label className="text-xs">GST %</Label><Input type="number" data-testid={`im-gst-${i}`} value={line.gst_percent} onChange={(e) => { const items = [...editing.items]; items[i] = { ...line, gst_percent: e.target.value }; setEditing({ ...editing, items }); }} /></div>
                <div><Label className="text-xs">Making</Label><Input type="number" value={line.making || 0} onChange={(e) => { const items = [...editing.items]; items[i] = { ...line, making: e.target.value }; setEditing({ ...editing, items }); }} /></div>
                <div><Label className="text-xs">Discount</Label><Input type="number" value={line.discount || 0} onChange={(e) => { const items = [...editing.items]; items[i] = { ...line, discount: e.target.value }; setEditing({ ...editing, items }); }} /></div>
                <div><Label className="text-xs">Gross wt</Label><Input value={line.gross_wt || ""} onChange={(e) => { const items = [...editing.items]; items[i] = { ...line, gross_wt: e.target.value }; setEditing({ ...editing, items }); }} /></div>
                <div><Label className="text-xs">Net wt</Label><Input value={line.net_wt || ""} onChange={(e) => { const items = [...editing.items]; items[i] = { ...line, net_wt: e.target.value }; setEditing({ ...editing, items }); }} /></div>
              </div>
            ))}
            {editing.design === "universal" && (
              <div className="rounded-xl border border-slate-200 p-3 space-y-3" data-testid="im-gst-switch">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
                  <input type="checkbox" checked={Boolean(editing.gst_enabled)} onChange={(e) => setEditing({ ...editing, gst_enabled: e.target.checked })} />
                  Add GST on the bill total
                </label>
                {editing.gst_enabled && (
                  <div className="max-w-xs">
                    <Label className="text-xs">GST %</Label>
                    <Input type="number" className="mt-1" data-testid="im-gst-percent" value={editing.gst_percent} onChange={(e) => setEditing({ ...editing, gst_percent: e.target.value })} />
                  </div>
                )}
              </div>
            )}
          </div>
          {editing.design === "jewellery" && (
            <div className="space-y-2" data-testid="im-old-gold">
              <Label>Old gold exchange</Label>
              {(editing.old_gold || []).map((row, i) => (
                <div key={i} className="grid grid-cols-2 sm:grid-cols-6 gap-2">
                  {[
                    ["description", "Description"], ["gross_wt", "Gross wt"], ["purity", "Purity %"],
                    ["net_wt", "Net wt"], ["rate", "Rate"], ["value", "Exchange value"],
                  ].map(([key, label]) => (
                    <div key={key}>
                      <Label className="text-xs">{label}</Label>
                      <Input value={row[key] || ""} onChange={(e) => {
                        const old_gold = [...editing.old_gold];
                        old_gold[i] = { ...row, [key]: e.target.value };
                        setEditing({ ...editing, old_gold });
                      }} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Round off</Label><Input type="number" className="mt-1" value={editing.round_off} onChange={(e) => setEditing({ ...editing, round_off: e.target.value })} /></div>
            <div><Label>Received</Label><Input type="number" className="mt-1" value={editing.received} onChange={(e) => setEditing({ ...editing, received: e.target.value })} /></div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={saveInvoice} disabled={saving} data-testid="im-save" className="bg-blue-600 hover:bg-blue-700">Save invoice</Button>
            <Button type="button" variant="outline" onClick={emailInvoice} data-testid="im-email"><Mail className="w-4 h-4 mr-1" /> Email invoice</Button>
            {editing.id && <Button type="button" variant="outline" data-testid="im-pdf" onClick={async () => {
              const res = await api.get(`/merchant/invoice-maker/${editing.id}/pdf`, { responseType: "blob" });
              const url = URL.createObjectURL(res.data);
              const a = document.createElement("a");
              a.href = url;
              a.download = `${editing.number || "invoice"}.pdf`;
              a.click();
              URL.revokeObjectURL(url);
            }}>Download PDF</Button>}
            {editing.link && <Button type="button" variant="outline" data-testid="im-copy-link" onClick={() => { navigator.clipboard.writeText(editing.link); toast.success("Link copied"); }}>Copy link</Button>}
            {editing.link && <p className="w-full text-xs break-all text-slate-500" data-testid="im-link">{editing.link}</p>}
            <Button type="button" variant="ghost" onClick={() => setTab("list")}>Back to list</Button>
          </div>
        </Card>
      )}

      {(tab === "universal" || tab === "jewellery") && settings && (
        <Card className="p-6 space-y-4 border-slate-200/70" data-testid={`im-settings-${tab}`}>
          <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
            <input type="radio" className="mt-1" name="invoice-design" data-testid={`im-use-${tab}`} checked={activeDesign === tab} onChange={() => chooseDesign(tab)} />
            <span>
              <span className="font-semibold text-slate-900">Use this design for every new bill</span>
              <span className="block text-slate-500">The other design turns off. You will not be asked to pick a design each time you create a bill.</span>
            </span>
          </label>
          {Object.entries(settings[tab]).filter(([key]) => !key.endsWith("_url")).map(([key, value]) => (
            <div key={key}>
              <Label className="capitalize">{key.replaceAll("_", " ")}</Label>
              {key === "terms" ? (
                <textarea rows={5} className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" value={value} onChange={(e) => setBrand(tab, key, e.target.value)} />
              ) : (
                <Input className="mt-1" value={value} onChange={(e) => setBrand(tab, key, e.target.value)} />
              )}
            </div>
          ))}
          {Object.keys(settings[tab]).filter((key) => key.endsWith("_url")).map((key) => (
            <div key={key}>
              <Label className="capitalize">{key.replaceAll("_", " ")}</Label>
              <Input type="file" accept="image/*" className="mt-1" data-testid={`im-upload-${tab}-${key}`} onChange={(e) => onImage(tab, key, e.target.files?.[0])} />
              {settings[tab][key] ? <img src={mediaUrl(settings[tab][key])} alt="" className="mt-2 h-16 object-contain" /> : null}
            </div>
          ))}
          <Button onClick={saveSettings} disabled={saving} data-testid="im-save-design" className="bg-blue-600 hover:bg-blue-700">Save design</Button>
        </Card>
      )}
    </div>
  );
}
